import React, { Component } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import Label from '../Label/Label';
import './input.scss';

export const INPUT_TYPES = {
  CHECKBOX: 'CHECKBOX',
  NUMBER: 'NUMBER',
  RADIO: 'RADIO',
  SELECT: 'SELECT',
  TEXT: 'TEXT',
  TEXTAREA: 'TEXTAREA',
};

class Input extends Component {
  static propTypes = {
    checked: PropTypes.bool,
    className: PropTypes.string,
    defaultHeight: PropTypes.bool,
    disabled: PropTypes.bool,
    error: PropTypes.string,
    id: PropTypes.string,
    inline: PropTypes.bool,
    inputClassName: PropTypes.string,
    label: PropTypes.any,
    labelClassName: PropTypes.string,
    max: PropTypes.number,
    min: PropTypes.number,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    options: PropTypes.array,
    placeholder: PropTypes.string,
    type: PropTypes.oneOf(Object.keys(INPUT_TYPES)),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    foreignComponent: PropTypes.any,
    inputWrapperClassName: PropTypes.string,
  };

  static defaultProps = {
    disabled: false,
    inline: false,
    type: 'text',
    max: Infinity,
    min: -Infinity,
    error: '',
    options: [],
    checked: false,
    defaultHeight: true,
  };

  constructor(props) {
    super(props);

    this.inputField = this.inputField.bind(this);
    this.onChange = this.onChange.bind(this);
  }

  onChange(event) {
    if (this.props.type !== INPUT_TYPES.NUMBER) {
      this.props.onChange(event);
      return;
    }

    const value = event.target.value;
    const numberTest = /^-?\d*\.?\d*$/;
    const valueIsNotAValidNumber = !numberTest.test(value);
    const numberValue = Number(value);
    const valueIsMoreThanMax = numberValue > this.props.max;
    const valueIsLessThanMin = numberValue < this.props.min;

    if (valueIsNotAValidNumber || valueIsMoreThanMax || valueIsLessThanMin) {
      return;
    }

    this.props.onChange(event);
  }

  inputField() {
    const commonProps = {
      disabled: this.props.disabled,
      id: this.props.id,
      name: this.props.name,
      onBlur: this.props.onBlur,
      onChange: this.onChange,
      onFocus: this.props.onFocus,
      placeholder: this.props.placeholder,
      value: this.props.value,
    };

    switch (this.props.type) {
      case INPUT_TYPES.RADIO:
        return (
          <input
            data-testid={commonProps.id}
            type="radio"
            className={cx(
              'input__field outline-none',
              this.props.inputClassName
            )}
            {...commonProps}
            checked={this.props.value === this.props.id}
          />
        );
      case INPUT_TYPES.CHECKBOX:
        return (
          <input
            data-testid={commonProps.id}
            type="checkbox"
            className={cx(
              'input__field outline-none cursor-pointer',
              this.props.inputClassName
            )}
            {...commonProps}
            checked={this.props.checked}
          />
        );
      case INPUT_TYPES.TEXTAREA:
        return (
          <textarea
            data-testid={commonProps.id}
            className={cx(
              'input__field shadow text-sm bg-white text-black rounded p-2 block w-full border-none outline-none resize-none',
              { defaultHeight: this.props.defaultHeight },
              this.props.inputClassName
            )}
            {...commonProps}
          />
        );
      case INPUT_TYPES.SELECT:
        return (
          <select
            data-testid={commonProps.id}
            className={cx(
              'input__field shadow text-sm bg-white text-black rounded px-2 block w-full border-none outline-none',
              { defaultHeight: this.props.defaultHeight },
              this.props.inputClassName
            )}
            {...commonProps}>
            <option value="" disabled>
              {this.props.placeholder}
            </option>
            {this.props.options.map(optionObject => {
              return (
                <option key={optionObject.label} value={optionObject.value}>
                  {optionObject.label}
                </option>
              );
            })}
          </select>
        );
      default:
        return (
          <input
            data-testid={commonProps.id}
            className={cx(
              'input__field shadow text-sm bg-white text-black rounded px-2 block w-full border-none outline-none',
              { defaultHeight: this.props.defaultHeight },
              this.props.inputClassName
            )}
            type="text"
            {...commonProps}
          />
        );
    }
  }

  shouldComponentUpdate(nextProps) {
    if (nextProps.foreignComponent || this.props.foreignComponent) {
      return true;
    }

    const primitiveProps = [
      'className',
      'disabled',
      'error',
      'id',
      'inline',
      'inputClassName',
      'label',
      'labelClassName',
      'max',
      'min',
      'name',
      'placeholder',
      'type',
      'value',
      'onChange',
      'onFocus',
      'onBlur',
      'checked',
      'inputWrapperClassName',
    ];

    const hasAnyPrimitivePropChanged = primitiveProps.some(
      propName => this.props[propName] !== nextProps[propName]
    );
    if (hasAnyPrimitivePropChanged) {
      return true;
    }

    if (this.props.type === INPUT_TYPES.SELECT) {
      if (this.props.options.length !== nextProps.options.length) {
        return true;
      }

      const hasAnyOptionChanged = this.props.options.some(
        (oldOption, index) => {
          const newOption = nextProps.options[index];
          return oldOption.value !== newOption.value;
        }
      );

      if (hasAnyOptionChanged) {
        return true;
      }
    }

    return false;
  }

  render() {
    const showError = Boolean(this.props.error.trim());
    const isLabelPresent = Boolean(this.props.label);
    return (
      <div
        className={cx('input_box', this.props.className, {
          disabled: this.props.disabled,
          'flex items-center justify-between': this.props.inline,
        })}>
        {isLabelPresent && (
          <Label
            htmlFor={this.props.id}
            text={this.props.label}
            className={this.props.labelClassName}
          />
        )}
        <div
          className={cx(
            'input_wrapper rounded',
            this.props.inputWrapperClassName,
            {
              error_box: showError,
              'mt-2': isLabelPresent && !this.props.inline,
              'flex-grow': this.props.inline,
            }
          )}>
          {this.props.foreignComponent}
          {this.inputField()}
        </div>
        {showError && (
          <div className="error_message text-red text-xs mt-1">
            {this.props.error}
          </div>
        )}
      </div>
    );
  }
}

export default  Input;
